/**
 * SPA entrypoint — DESIGN.md §13.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { createQueryClient } from './lib/query';
import { startErrorTracing } from './lib/sentry';
import { ThemeProvider } from './lib/theme';
import './index.css';

// Not awaited: this asks the API whether tracing is configured, and a slow or
// unreachable API must never be the reason the app fails to paint. The SDK's
// global handlers pick up anything thrown after it lands, and the errors that
// happen in the first few milliseconds are the ones a stack trace explains
// least well anyway.
void startErrorTracing();

const queryClient = createQueryClient();

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // The router does not need to re-run loaders that TanStack Query already
  // caches (§13.2 — Query owns server state).
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Missing #root element');

createRoot(rootElement).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
